import React, { useState } from "react";
import { Box, ButtonBase, Collapse, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ToolResultEvent, ToolUseEvent } from "../../../types/ai-event";
import { CasLoader } from "../../brand/CasLoader";
import { ToolCallCard } from "./ToolCallCard";

interface ToolCallGroupProps {
  calls: Array<{ event: ToolUseEvent; index: number }>;
  toolResults: Map<string, ToolResultEvent>;
  lastIndex: number;
  // The Background button of a running Bash call, passed to each card.
  onBackground?: (toolUseId: string) => void;
}

// A run of two or more consecutive tool calls as one line, "N tool calls" (spec D3, U.1). Collapsed by default; the cards stay in the DOM
// inside the Collapse, as in ToolCallCard. A call is running while it has no result and nothing came after it. One call is its own card with no
// group line over it, so its Background button is in view while it runs (spec 2026-10-27-claude-native-background-tasks, U2).
export const ToolCallGroup: React.FC<ToolCallGroupProps> = ({ calls, toolResults, lastIndex, onBackground }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  if (calls.length === 1) {
    const only = calls[0];
    return <ToolCallCard event={only.event} result={toolResults.get(only.event.id)} done={!toolResults.get(only.event.id) && only.index < lastIndex} onBackground={onBackground} />;
  }
  const running = calls.some((c) => !toolResults.get(c.event.id) && c.index >= lastIndex);
  const label = `${calls.length} tool calls`;
  return (
    <Box
      data-testid="tool-group"
      data-running={running ? "true" : "false"}
      sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, my: 0.5, backgroundColor: theme.palette.background.paper, flexShrink: 0 }}
    >
      <ButtonBase
        data-testid="tool-group-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, justifyContent: "flex-start" }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {label}
        </Typography>
        {running && <CasLoader size={16} label="Tool running" />}
        <Box sx={{ flex: 1 }} />
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </ButtonBase>
      <Collapse in={expanded}>
        <Box sx={{ px: 1, pb: 0.5 }}>
          {calls.map((c) => (
            <ToolCallCard key={c.event.id} event={c.event} result={toolResults.get(c.event.id)} done={!toolResults.get(c.event.id) && c.index < lastIndex} onBackground={onBackground} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
